// invoiceSaga.js
import { call, put, takeLatest } from "redux-saga/effects";

import {
  FETCH_INVOICES_REQUEST,
  FETCH_INVOICES_SUCCESS,
  FETCH_INVOICES_FAILURE,
  CREATE_INVOICE_REQUEST,
  CREATE_INVOICE_SUCCESS,
  CREATE_INVOICE_FAILURE,
  INVOICE_DETAIL_REQUEST,
  INVOICE_DETAIL_SUCCESS,
  INVOICE_DETAIL_FAILURE,
  UPDATE_INVOICE_REQUEST,
  UPDATE_INVOICE_SUCCESS,
  UPDATE_INVOICE_FAILURE,
  DELETE_INVOICE_REQUEST,
  DELETE_INVOICE_SUCCESS,
  DELETE_INVOICE_FAILURE,
} from "../Actions/Actions";
import Axios from "../../utils/Axios";
import AuthHandler from "../../utils/AuthHandler";
import { navigate } from "../../utils/navigation";
import {
  INVOICE_LIST,
  INVOICE_LIST_BY_PROJECT,
  NEW_INVOICE,
  INVOICE_DETAIL,
  EDIT_INVOICE,
  DELETE_INVOICE,
} from "../../config/API_Collection";
import { getErrorMessage } from "../../config/Error_Codes";

// Fetch invoices saga
function* fetchInvoicesSaga({ payload }) {
  try {
    // If a project_id is provided, use the project-specific endpoint
    const useByProject = payload && (payload.project_id || payload.projectId);
    const endpoint = useByProject ? INVOICE_LIST_BY_PROJECT : INVOICE_LIST;
    const params = {};
    if (payload) {
      // normalize param names to backend expected keys
      if (payload.project_id) params.project_id = payload.project_id;
      else if (payload.projectId) params.project_id = payload.projectId;
      if (payload.client_id) params.client_id = payload.client_id;
      else if (payload.clientId) params.client_id = payload.clientId;
    }

    console.debug('[fetchInvoicesSaga] calling', endpoint, 'with params', params);
    // Build the final request URI for easier debugging
    try {
      const uri = Axios.getUri({ url: endpoint, params });
      console.debug('[fetchInvoicesSaga] request uri ->', uri);
    } catch (e) {
      // ignore if getUri not available
    }

    const response = yield call(Axios.get, endpoint, { params });
    console.debug('[fetchInvoicesSaga] response.data ->', response.data);

    // Normalize different possible API response shapes to an array
    let invoices = [];
    const respData = response.data;
    if (Array.isArray(respData)) {
      invoices = respData;
    } else if (Array.isArray(respData?.data)) {
      invoices = respData.data;
    } else if (Array.isArray(respData?.invoices)) {
      invoices = respData.invoices;
    } else if (Array.isArray(respData?.result)) {
      invoices = respData.result;
    } else if (Array.isArray(respData?.data?.invoices)) {
      // API returns { data: { invoices: [...] } } as in Postman screenshot
      invoices = respData.data.invoices;
    } else if (Array.isArray(respData?.data?.data?.invoices)) {
      // defensive: sometimes nested further
      invoices = respData.data.data.invoices;
    } else {
      // fallback: no invoice array found — log keys for debugging
      console.warn('[fetchInvoicesSaga] unexpected response shape, keys:', Object.keys(respData || {}));
      invoices = [];
    }

    yield put({ type: FETCH_INVOICES_SUCCESS, payload: invoices });
  } catch (error) {
    yield call(AuthHandler, error.response?.status, null, navigate);
    yield put({
      type: FETCH_INVOICES_FAILURE,
      payload: getErrorMessage(error.response?.status),
    });
  }
}

// Create invoice saga
function* createInvoiceSaga({ payload }) {
  try {
    console.log("Creating invoice with payload:", payload); // Debug log
    const response = yield call(Axios.post, NEW_INVOICE, payload);
    
    console.log("Invoice creation response:", response.data); // Debug log
    const newInvoice = response.data.data || response.data;
    yield put({ type: CREATE_INVOICE_SUCCESS, payload: newInvoice });
  } catch (error) {
    console.error("Invoice creation error:", error.response?.data || error.message); // Debug log
    
    // Only call AuthHandler for auth-related errors (401, 403)
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Skip AuthHandler to avoid setToast error for now
      // yield call(AuthHandler, error.response?.status, null, navigate);
    }
    
    // Get more specific error message from API response
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        getErrorMessage(error.response?.status);
    
    yield put({
      type: CREATE_INVOICE_FAILURE,
      payload: errorMessage,
    });
  }
}

// Get invoice detail saga
function* invoiceDetailSaga({ payload }) {
  try {
    const response = yield call(Axios.get, `${INVOICE_DETAIL}?id=${payload.id}`);
    
    const invoiceDetail = response.data.data || response.data;
    yield put({ type: INVOICE_DETAIL_SUCCESS, payload: invoiceDetail });
  } catch (error) {
    yield call(AuthHandler, error.response?.status, null, navigate);
    yield put({
      type: INVOICE_DETAIL_FAILURE,
      payload: getErrorMessage(error.response?.status),
    });
  }
}

// Update invoice saga
function* updateInvoiceSaga({ payload }) {
  try {
    const response = yield call(Axios.post, EDIT_INVOICE, payload);
    
    const updatedInvoice = response.data.data || response.data;
    yield put({ type: UPDATE_INVOICE_SUCCESS, payload: updatedInvoice });
  } catch (error) {
    yield call(AuthHandler, error.response?.status, null, navigate);
    yield put({
      type: UPDATE_INVOICE_FAILURE,
      payload: getErrorMessage(error.response?.status),
    });
  }
}

// Delete invoice saga
function* deleteInvoiceSaga({ payload }) {
  try {
    const response = yield call(Axios.post, DELETE_INVOICE, { invoice_id: payload.invoice_id });

    const msg = response?.data?.message || "Invoice deleted successfully";
    yield put({ type: DELETE_INVOICE_SUCCESS, payload: { invoice_id: payload.invoice_id, message: msg } });
  } catch (error) {
    yield call(AuthHandler, error.response?.status, null, navigate);
    yield put({
      type: DELETE_INVOICE_FAILURE,
      payload: getErrorMessage(error.response?.status),
    });
  }
}

export default function* invoiceWatcher() {
  yield takeLatest(FETCH_INVOICES_REQUEST, fetchInvoicesSaga);
  yield takeLatest(CREATE_INVOICE_REQUEST, createInvoiceSaga);
  yield takeLatest(INVOICE_DETAIL_REQUEST, invoiceDetailSaga);
  yield takeLatest(UPDATE_INVOICE_REQUEST, updateInvoiceSaga);
  yield takeLatest(DELETE_INVOICE_REQUEST, deleteInvoiceSaga);
}